import { useTRPC } from '@/utils/trpc';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { DocumentReference, documentTypeLabel } from '@liza/ui/components/records';
import { DATE_FORMATS } from '@liza/ui/utils/date';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

type Props = {
  id: string;
};

export const Document = ({ id }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  // Attachment URLs are presigned and expire (~60 min), so always fetch the document fresh
  // instead of reusing cached list data (same rationale as FaxViewerDialog).
  const documentQuery = useQuery({
    ...trpc.patient.documentReference.get.queryOptions({ id }),
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <QueryStateHandler query={documentQuery} emptyMessage={t('noDocumentsFound')}>
      {(data) => {
        const attachment = data.documentReference.content?.[0]?.attachment;
        const metadata = [
          data.documentReference.date
            ? format(parseISO(data.documentReference.date), DATE_FORMATS.DATE_TIME)
            : undefined,
          documentTypeLabel(attachment?.contentType, t),
          data.documentReference.author?.[0]?.display,
        ]
          .filter(Boolean)
          .join(' · ');
        return (
          <div className="flex h-full flex-col gap-3 p-4" data-testid="document-detail">
            <div>
              <h2 className="text-sm font-medium">{attachment?.title ?? t('document')}</h2>
              {metadata && <p className="text-xs text-muted-foreground">{metadata}</p>}
            </div>
            <div className="min-h-0 flex-1">
              <DocumentReference documentReference={data.documentReference} fill />
            </div>
          </div>
        );
      }}
    </QueryStateHandler>
  );
};
